// Visor de detecciones: foto con cajas + recortes de cada objeto con su confianza.
// Pasar el mouse por una caja marca su recorte y viceversa; clic en un recorte = zoom a ese objeto.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { classInfo, isPerson, isMachine, photoStats, photoUrl, fmtDMY, fmtHM, ALL_CLASSES, setFix } from './iaData';
import { T, Btn, Seg } from './ui';

const clampT = (t, s) => Math.min(0, Math.max((1 - s) * 100, t));

const FILTERS = [
  { value: 'todas', label: 'Todas' },
  { value: 'gente', label: 'Con personas' },
  { value: 'casco', label: 'Personas sin casco' },
  { value: 'maquina', label: 'Con maquinaria' },
];

export default function DetectionsPanel({ photos: allPhotos, cfg, onFix, onReview, internal }) {
  const [filtro, setFiltro] = useState('todas');
  const photos = useMemo(() => {
    if (filtro === 'todas') return allPhotos;
    return allPhotos.filter((p) => {
      const ds = photoStats(p, cfg).dets;
      if (filtro === 'gente') return ds.some((d) => isPerson(d.cls));
      if (filtro === 'casco') return ds.some((d) => d.cls === 'Persona sin casco');
      return ds.some((d) => isMachine(d.cls));
    });
  }, [allPhotos, cfg, filtro]);
  const best = useMemo(() => {
    let bi = 0, bv = -1;
    photos.forEach((p, i) => { const v = photoStats(p, cfg).total; if (v >= bv) { bv = v; bi = i; } });
    return bi;
  }, [photos, cfg]);
  const [idx, setIdx] = useState(best);
  useEffect(() => setIdx(best), [best]);

  const photo = photos[Math.min(idx, photos.length - 1)];
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [hover, setHover] = useState(null);
  const [zoom, setZoom] = useState({ s: 1, tx: 0, ty: 0 });
  const boxRef = useRef(null);

  useEffect(() => {
    if (!photo) return;
    let alive = true;
    setUrl(null); setErr(false); setZoom({ s: 1, tx: 0, ty: 0 }); setHover(null);
    photoUrl(photo).then((u) => alive && setUrl(u)).catch(() => alive && setErr(true));
    [photos[idx - 1], photos[idx + 1]].forEach((p) => p && photoUrl(p).catch(() => {}));
    return () => { alive = false; };
  }, [photo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Zoom con la rueda, centrado en el puntero
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const px = ((e.clientX - r.left) / r.width) * 100, py = ((e.clientY - r.top) / r.height) * 100;
      setZoom((z) => {
        const s = Math.min(8, Math.max(1, z.s * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
        return { s, tx: clampT(px - (px - z.tx) * (s / z.s), s), ty: clampT(py - (py - z.ty) * (s / z.s), s) };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [photo]);

  const bar = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
      <Seg value={filtro} onChange={(v) => { setFiltro(v); setIdx(0); }} options={FILTERS} />
      <span style={{ fontSize: 12, color: T.muted }}>{photos.length} {photos.length === 1 ? 'foto' : 'fotos'}</span>
      <div style={{ flex: 1 }} />
      {internal && <Btn onClick={onReview} title="Herramienta interna: corregir el casco para reentrenar">⛑ Revisar cascos (interno)</Btn>}
    </div>
  );
  if (!photos.length) return <div>{bar}<div style={{ color: T.muted, padding: 24, textAlign: 'center' }}>No hay fotos con ese filtro en este intervalo.</div></div>;

  const st = photoStats(photo, cfg);
  const dets = st.dets.map((d, i) => ({ ...d, i })).filter((d) => d.x2 != null);
  const order = [...dets].sort((a, b) => ALL_CLASSES.indexOf(a.cls) - ALL_CLASSES.indexOf(b.cls) || b.conf - a.conf);

  const focusDet = (d) => {
    const bw = (d.x2 - d.x1) / photo.w, bh = (d.y2 - d.y1) / photo.h;
    const s = Math.min(6, Math.max(1.5, 0.35 / Math.max(bw, bh)));
    const cx = ((d.x1 + d.x2) / 2 / photo.w) * 100, cy = ((d.y1 + d.y2) / 2 / photo.h) * 100;
    setZoom({ s, tx: clampT(50 - cx * s, s), ty: clampT(50 - cy * s, s) });
    setHover(d.i);
  };

  const go = (i) => setIdx(Math.max(0, Math.min(photos.length - 1, i)));
  const sw = Math.max(2, photo.w / 640);

  const fix = (d, val) => { setFix(photo, { ...d, cls: d.orig || d.cls }, d.fixed && val === (d.cls === 'Persona con casco' ? 'con' : 'sin') ? null : val); onFix && onFix(); };

  return (
    <div>
    {bar}
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.2fr) minmax(0, 1fr)', gap: 14 }} className="iad-det-grid">
      <div style={{ minWidth: 0 }}>
        <div ref={boxRef} style={{ position: 'relative', width: '100%', aspectRatio: `${photo.w} / ${photo.h}`, overflow: 'hidden', borderRadius: 12, background: '#000' }}>
          <div style={{ position: 'absolute', inset: 0, transformOrigin: '0 0', transform: `translate(${zoom.tx}%, ${zoom.ty}%) scale(${zoom.s})`, transition: 'transform .35s ease' }}>
            {url && <img src={url} alt="" draggable={false} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'fill' }} onError={() => setErr(true)} />}
            {url && (
              <svg viewBox={`0 0 ${photo.w} ${photo.h}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                {dets.map((d) => {
                  const c = classInfo(d.cls).color, on = hover === d.i;
                  return (
                    <g key={d.i} onMouseEnter={() => setHover(d.i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }} onClick={() => focusDet(d)}>
                      <rect x={d.x1} y={d.y1} width={d.x2 - d.x1} height={d.y2 - d.y1} fill={on ? c + '33' : 'transparent'} stroke={c} strokeWidth={on ? sw * 2 : sw} opacity={hover == null || on ? 1 : 0.45} />
                      {(on || dets.length <= 8) && (
                        <text x={d.x1} y={d.y2 + sw * 9} fill={c} fontSize={sw * 9} fontWeight="700" style={{ paintOrder: 'stroke', stroke: '#000', strokeWidth: sw * 2.5 }}>
                          {classInfo(d.cls).label}{internal && d.static ? ' · quieto' : ''}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            )}
          </div>
          {!url && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: T.muted }}>{err ? 'No se pudo cargar la foto' : 'Cargando foto…'}</div>}
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 5, flexWrap: 'wrap', maxWidth: '85%' }}>
            {Object.entries(st.counts).sort((a, b) => ALL_CLASSES.indexOf(a[0]) - ALL_CLASSES.indexOf(b[0])).map(([c, n]) => (
              <span key={c} style={{ background: 'rgba(255,255,255,.92)', color: classInfo(c).color, border: `1px solid ${classInfo(c).color}`, borderRadius: 999, padding: '1px 9px', fontSize: 12, fontWeight: 700 }}>
                {classInfo(c).label} {n}
              </span>
            ))}
          </div>
          {zoom.s > 1 && (
            <button type="button" onClick={() => setZoom({ s: 1, tx: 0, ty: 0 })} style={{ position: 'absolute', top: 10, right: 10, background: '#000b', color: '#fff', border: `1px solid ${T.line}`, borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
              Ver foto completa
            </button>
          )}
          {!photo.hasBoxes && (
            <div style={{ position: 'absolute', bottom: 10, left: 10, right: 10, background: '#000b', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 12 }}>
              Foto anterior al 30/05/2026: solo tenemos los conteos, sin la ubicación de cada objeto.
            </div>
          )}
        </div>

        <input type="range" min={0} max={photos.length - 1} value={idx} onChange={(e) => go(+e.target.value)} style={{ width: '100%', marginTop: 12, accentColor: T.blue }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: T.text }}>
          <Btn onClick={() => go(0)} title="Primera">⏮</Btn>
          <Btn onClick={() => go(idx - 1)} title="Anterior">◀</Btn>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <b>{idx + 1}</b> / {photos.length} · {fmtDMY(photo.ms)} {fmtHM(photo.ms)} · <b>{st.total}</b> {st.total === 1 ? 'detección' : 'detecciones'}
          </span>
          <Btn onClick={() => go(idx + 1)} title="Siguiente">▶</Btn>
          <Btn onClick={() => go(photos.length - 1)} title="Última">⏭</Btn>
        </div>
      </div>

      <div style={{ minWidth: 0, maxHeight: 560, overflowY: 'auto', paddingRight: 4 }}>
        {!order.length && <div style={{ color: T.muted, fontSize: 13 }}>{photo.hasBoxes ? 'Nada detectado en esta foto con los filtros actuales.' : 'Sin recortes para esta foto.'}</div>}
        <div style={{ columnWidth: 104, columnGap: 8 }}>
          {order.map((d) => <Crop key={d.i} d={d} photo={photo} url={url} on={hover === d.i} blur={cfg.blurPeople && isPerson(d.cls)} internal={internal}
            onEnter={() => setHover(d.i)} onLeave={() => setHover(null)} onClick={() => focusDet(d)} onFix={(v) => fix(d, v)} />)}
        </div>
      </div>
    </div>
    </div>
  );
}

function Crop({ d, photo, url, on, blur, onEnter, onLeave, onClick, onFix, internal }) {
  const c = classInfo(d.cls).color;
  const padX = (d.x2 - d.x1) * 0.15, padY = (d.y2 - d.y1) * 0.15;
  const x1 = Math.max(0, d.x1 - padX), y1 = Math.max(0, d.y1 - padY);
  const bw = Math.min(photo.w, d.x2 + padX) - x1, bh = Math.min(photo.h, d.y2 + padY) - y1;
  const W = 104, s = W / bw, H = Math.min(190, Math.max(56, bh * s));
  return (
    <div onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={onClick}
      style={{ breakInside: 'avoid', marginBottom: 8, borderRadius: 10, overflow: 'hidden', border: `2px solid ${on ? T.text : c}`, cursor: 'pointer', position: 'relative', background: '#000', boxShadow: on ? `0 0 0 3px ${c}55` : '0 1px 2px rgba(0,0,0,.08)' }}>
      <div style={{
        width: '100%', height: H,
        backgroundImage: url ? `url("${url}")` : 'none', backgroundRepeat: 'no-repeat',
        backgroundSize: `${photo.w * s}px ${photo.h * s}px`, backgroundPosition: `${-x1 * s}px ${-y1 * s}px`,
        filter: blur ? 'blur(5px)' : 'none',
      }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 6px 4px', background: 'linear-gradient(transparent, #000d)', fontSize: 11, fontWeight: 700, color: '#fff' }}>
        {classInfo(d.cls).label}{internal && <span style={{ color: d.conf >= 0.6 ? T.green : d.conf >= 0.4 ? T.yellow : T.red }}> / {d.conf.toFixed(2)}</span>}
        {internal && isPerson(d.cls) && (
          <div style={{ marginTop: 4, display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" title="Tiene casco" onClick={() => onFix('con')} style={fixBtn(d.cls === 'Persona con casco', T.green)}>⛑ sí</button>
            <button type="button" title="No tiene casco" onClick={() => onFix('sin')} style={fixBtn(d.cls === 'Persona sin casco', T.red)}>sin</button>
            <button type="button" title="No es una persona" onClick={() => onFix('no')} style={fixBtn(false, '#9ca3af')}>✕</button>
          </div>
        )}
        {internal && d.fixed && <div style={{ color: '#a7f3d0', fontWeight: 600 }}>✓ revisado</div>}
        {internal && d.static && <div style={{ color: T.muted, fontWeight: 600 }}>quieto</div>}
      </div>
    </div>
  );
}

const fixBtn = (on, c) => ({ padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', border: `1px solid ${c}`, background: on ? c : 'rgba(0,0,0,.45)', color: '#fff' });
